import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import { useLanguage } from '../../lib/i18n/LanguageContext';
import { fetchSiteSettings } from '../../lib/supabase/api';
import {
  generateAltText,
  optimizePexelsUrl,
  generatePexelsSrcSet,
} from '../../lib/utils/imageOptimization';
import SectionTitle from '../../components/ui/SectionTitle';
import GradientBorderButton from '../../components/ui/GradientBorderButton';

const AboutPreview = () => {
  const { t } = useLanguage();
  const [aboutImages, setAboutImages] = useState<string[]>([
    'https://images.pexels.com/photos/1566837/pexels-photo-1566837.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
    'https://images.pexels.com/photos/5639411/pexels-photo-5639411.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
    'https://images.pexels.com/photos/1279330/pexels-photo-1279330.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
    'https://images.pexels.com/photos/544113/pexels-photo-544113.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
  ]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    const loadSiteSettings = async () => {
      try {
        setIsLoading(true);

        const settings = await fetchSiteSettings();

        if (settings) {
          const newImages = [
            settings.about_image_1_url || aboutImages[0],
            settings.about_image_2_url || aboutImages[1],
            settings.about_image_3_url || aboutImages[2],
            settings.about_image_4_url || aboutImages[3],
          ];
          setAboutImages(newImages);
        }
      } catch (err) {
        console.error('Failed to load about section images:', err);
        // Keep the default images on error
      } finally {
        setIsLoading(false);
      }
    };

    loadSiteSettings();
  }, []);

  return (
    <section id="about-preview" className="py-20 bg-white">
      <div className="container-custom">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="space-y-6 w-full"
          >
            <SectionTitle title={t.home.aboutPreview.title} align="left" className="mb-6">
              <p className="text-neutral-700 mt-4">{t.home.aboutPreview.subtitle}</p>
            </SectionTitle>

            <p className="text-neutral-600 leading-relaxed">{t.home.aboutPreview.content[0]}</p>

            <p className="text-neutral-600 leading-relaxed">{t.home.aboutPreview.content[1]}</p>

            <div className="pt-4">
              <GradientBorderButton onClick={() => (window.location.href = '/about')}>
                {t.home.aboutPreview.button}
              </GradientBorderButton>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative w-full"
          >
            {isLoading ? (
              <div className="grid grid-cols-2 gap-2 sm:gap-4 place-items-center">
                <div className="rounded-lg h-24 sm:h-32 md:h-48 lg:h-64 w-full bg-neutral-200 animate-pulse flex items-center justify-center">
                  <Loader2 className="w-6 h-6 text-neutral-400 animate-spin" />
                </div>
                <div className="rounded-lg h-24 sm:h-32 md:h-48 lg:h-64 w-full bg-neutral-200 animate-pulse flex items-center justify-center">
                  <Loader2 className="w-6 h-6 text-neutral-400 animate-spin" />
                </div>
                <div className="rounded-lg h-24 sm:h-32 md:h-48 lg:h-64 w-full bg-neutral-200 animate-pulse flex items-center justify-center">
                  <Loader2 className="w-6 h-6 text-neutral-400 animate-spin" />
                </div>
                <div className="rounded-lg h-24 sm:h-32 md:h-48 lg:h-64 w-full bg-neutral-200 animate-pulse flex items-center justify-center">
                  <Loader2 className="w-6 h-6 text-neutral-400 animate-spin" />
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2 sm:gap-4">
                <img
                  src={optimizePexelsUrl(aboutImages[0], 800, 600)}
                  srcSet={generatePexelsSrcSet(aboutImages[0])}
                  sizes="(max-width: 768px) 50vw, (max-width: 1200px) 25vw, 20vw"
                  alt={generateAltText('staff', 'Italian chefs preparing pizza dough')}
                  title="Professional Italian chefs at Rossopomodoro Reykjavik"
                  loading="eager"
                  width="400"
                  height="300"
                  className="rounded-lg h-24 sm:h-32 md:h-48 lg:h-64 w-full object-cover shadow-md"
                />
                <img
                  src={optimizePexelsUrl(aboutImages[1], 800, 600)}
                  srcSet={generatePexelsSrcSet(aboutImages[1])}
                  sizes="(max-width: 768px) 50vw, (max-width: 1200px) 25vw, 20vw"
                  alt={generateAltText('food', 'Fresh ingredients for authentic Italian cuisine')}
                  title="Premium Italian ingredients at Rossopomodoro Reykjavik"
                  loading="lazy"
                  width="400"
                  height="300"
                  className="rounded-lg h-24 sm:h-32 md:h-48 lg:h-64 w-full object-cover shadow-md"
                />
                <img
                  src={optimizePexelsUrl(aboutImages[2], 800, 600)}
                  srcSet={generatePexelsSrcSet(aboutImages[2])}
                  sizes="(max-width: 768px) 50vw, (max-width: 1200px) 25vw, 20vw"
                  alt={generateAltText('food', 'Authentic Italian pasta dish')}
                  title="Fresh pasta dishes at Rossopomodoro Reykjavik"
                  loading="lazy"
                  width="400"
                  height="300"
                  className="rounded-lg h-24 sm:h-32 md:h-48 lg:h-64 w-full object-cover shadow-md"
                />
                <img
                  src={optimizePexelsUrl(aboutImages[3], 800, 600)}
                  srcSet={generatePexelsSrcSet(aboutImages[3])}
                  sizes="(max-width: 768px) 50vw, (max-width: 1200px) 25vw, 20vw"
                  alt={generateAltText('interior', 'Elegant dining room atmosphere')}
                  title="Cozy Italian restaurant interior at Rossopomodoro Reykjavik"
                  loading="lazy"
                  width="400"
                  height="300"
                  className="rounded-lg h-24 sm:h-32 md:h-48 lg:h-64 w-full object-cover shadow-md"
                />
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AboutPreview;
